import * as z from "zod";

import { RefreshTypesSchema } from "Pages/ItemCountdown/schema";
import {
  ScheduledTimeSchema,
  WeeklyScheduleSchema as ShallowWeeklyScheduleSchema,
} from "Pages/OnlineOrderingSchedule/schema";

export const ModGroupTypeSchema = z.enum(["mandatory", "optional"]);
export type ModGroupType = z.infer<typeof ModGroupTypeSchema>;

export const PriceUnitSchema = z.enum([
  "item",
  "kilogram",
  "100 gram",
  "gram",
  "litre",
  "millilitre",
  "open",
]);
export type PriceUnit = z.infer<typeof PriceUnitSchema>;

export const ItemModSchema = z.object({
  name: z.string(),
  salesPrice: z.coerce.number(),
  salesPriceUuid: z.string(),
  sortOrder: z.number(),
  favourite: z.boolean(),
  uuid: z.string().toLowerCase(),
  itemModType: z.string(),
});

export const TableSchema = z.object({
  uuid: z.string().toLowerCase(),
  name: z.string(),
  floorLayoutUuid: z.string().toLowerCase(),
  floorLayoutName: z.string().toLowerCase(),
});
export type Table = z.infer<typeof TableSchema>;

export type ItemMod = z.infer<typeof ItemModSchema>;
export const PricingTypeSchema = z.enum([
  "simple",
  "default_mods",
  "mods_list",
]);
export type PricingType = z.infer<typeof PricingTypeSchema>;

export const ItemModGroupSchema = z.object({
  name: z.string(),
  sortOrder: z.number(),
  modGroupType: ModGroupTypeSchema,
  uuid: z.string().toLowerCase(),
  itemMods: z.array(ItemModSchema),
});
export type ItemModGroup = z.infer<typeof ItemModGroupSchema>;

export const ItemSchema = z.object({
  uuid: z.string().toLowerCase(),
  name: z.string(),
  marketingBlurb: z.string().nullish(),
  dietaries: z.array(z.string()),
  salesPrice: z.coerce.number(),
  salesPriceUuid: z.string().toLowerCase(),
  heroImageUrl: z.string().nullish(),
  heroImageThumbnailUrl: z.string().nullish(),
  status: z.string(),
  detail: z.string().nullish(),
  priceUnit: PriceUnitSchema,
  itemModGroups: z.array(ItemModGroupSchema),
});
export type Item = z.infer<typeof ItemSchema>;

export const SectionSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  pricingType: z.preprocess(
    () => "simple",
    PricingTypeSchema.default("simple"),
  ), // Hard pricing type to simple as its not used here
  footer: z.string().nullish(),
  sectionBlurb: z.string().nullish(),
  items: z.array(ItemSchema),
});
export type Section = z.infer<typeof SectionSchema>;

export const PromotionSchema = z.object({
  uuid: z.string().uuid(),
  itemUuid: z.string(),
});
export type Promotion = z.infer<typeof PromotionSchema>;

export const MenuSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  updatedAt: z.string(),
  sections: z.array(SectionSchema),
  promotions: z.array(PromotionSchema),
});
export type Menu = z.infer<typeof MenuSchema>;

export const BrandStyleSchema = z.enum([
  "brutalist",
  "simple",
  "elegant",
  "playful",
]);
export type BrandStyle = z.infer<typeof BrandStyleSchema>;

export const CurrencyDisplayTypeSchema = z.enum([
  "normal",
  "normal_with_symbol",
  "terse",
  "terse_with_symbol",
]);
export const OnlineMenuMessageSchema = z.object({
  title: z.string(),
  message: z.string(),
  confirmationText: z.string(),
});

export const FontSchema = z.enum(["font-sans", "font-serif", "font-mono"]);
export const VenueBrandSchema = z.object({
  brandColor: z.string().default("#000000"), // Black
  secondaryColor: z.string().default("#ffffff"), //Light Blue Grey
  tertiaryColor: z.string().default("#F2f2f2"), // White
  highlightColor: z.string().default("#df0202"), // Red
  brandStyle: BrandStyleSchema.default("simple"),
  currencyDisplayType: CurrencyDisplayTypeSchema.default("normal"),
  font: FontSchema.default("font-sans"),
  description: z.string().nullish(),
  activeMenuMessage: OnlineMenuMessageSchema.nullish(),
  onlineOrderingWeeklyScheduleUuid: z.string().nullish(),
});
export type VenueBrand = z.infer<typeof VenueBrandSchema>;

export const EnabledFeaturesSchema = z.object({
  onlineOrderingMenu: z.boolean().default(false),
  orderingEnabled: z.boolean().default(false),
  onlineSimpleOrdering: z.boolean().default(false),
  tableServiceEnabled: z.boolean().default(false),
  checkoutEnabled: z.boolean().default(false),
  tablePayOrdering: z.boolean().default(false),
  tablePay: z.boolean().default(false),
  billSplit: z.boolean().default(false),
  bustleBranding: z.boolean().default(false),
});
export type EnabledFeatures = z.infer<typeof EnabledFeaturesSchema>;

export const VenueSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  venueBrand: z.preprocess((v) => v ?? {}, VenueBrandSchema),
  instagramUserName: z.string().nullish(),
  facebookPageUrl: z.string().nullish(),
  twitterUserName: z.string().nullish(),
  logoImageUrl: z.string(),
  heroImageUrl: z.string(),
  companyWebsite: z.string().nullish(),
  openingHours: z.string().nullish(),
  address: z.string().nullish(),
  companyPhone: z.string().nullish(),
  locale: z.string(),
  enabledFeatures: EnabledFeaturesSchema,
  tables: z.array(TableSchema),
});

export type Venue = z.infer<typeof VenueSchema>;

export const WeeklyScheduleSchema = ShallowWeeklyScheduleSchema.extend({
  scheduledTimes: z.array(ScheduledTimeSchema),
});
export type WeeklySchedule = z.infer<typeof WeeklyScheduleSchema>;

export const ItemCountSchema = z
  .object({
    id: z.number(),
    itemId: z.number(),
    itemUuid: z.string(),
    itemName: z.string(),
    refreshType: RefreshTypesSchema,
    startingCount: z.coerce.number(),
    startAt: z.string(),
    discardedAt: z.string().nullable(),
    totalMoved: z.coerce.number(),
  })
  .transform((v) => ({
    ...v,
    remainingCount: v.startingCount - v.totalMoved,
  }));
export const ItemCountsSchema = z.array(ItemCountSchema);
export type ItemCount = z.infer<typeof ItemCountSchema>;
